/**
 * Honorarios — Historial: control de facturas emitidas/cobradas/pendientes + Excel anual.
 * Selector de origen: Facturados | Otros conceptos | Clientes eventuales | Acumulado.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DespachoKpiCard,
  DespachoSectionCard,
  DespachoSimpleTable,
} from "@/components/despacho/DespachoUi";
import {
  collectionStatusLabel,
  honorariosApi,
  parseHonorariosError,
} from "@/modules/despacho/despachoHonorariosApi";

const MONTH_KEYS = ["m01", "m02", "m03", "m04", "m05", "m06", "m07", "m08", "m09", "m10", "m11", "m12"];
const MONTH_LABELS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const CONTROL_SOURCES = [
  { value: "facturados", label: "Facturados" },
  { value: "otros_pagos", label: "Otros conceptos" },
  { value: "eventuales", label: "Clientes eventuales" },
  { value: "acumulado", label: "Acumulado" },
];

const SOURCE_HINTS = {
  facturados: "CFDI de clientes Factura / Convenio / Cortesía (sin eventuales ni otros conceptos).",
  otros_pagos: "Solo Clientes otros conceptos y registros de Otros cobros (sin eventuales).",
  eventuales: "CFDI de Clientes eventuales (despacho) y Eventuales act. empresarial.",
  acumulado: "Facturados + Otros conceptos + Clientes eventuales.",
};

const ORIGIN_LABELS = {
  facturados: "Facturados",
  otros_pagos: "Otros conceptos",
  eventuales: "Clientes eventuales",
};

function formatMoney(value, canView) {
  if (!canView || value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function downloadBlobResponse(response, fallbackName) {
  const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data || ""]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const disposition = response?.headers?.["content-disposition"] || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  link.href = url;
  link.download = match?.[1] || fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function HonorariosHistorialPanel({
  canViewAmounts = true,
  canExport = false,
}) {
  const yearOptions = useMemo(() => {
    const end = new Date().getFullYear() + 1;
    const years = [];
    for (let y = end; y >= 2020; y -= 1) years.push(String(y));
    return years;
  }, []);

  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [source, setSource] = useState("facturados");
  const [loading, setLoading] = useState(true);
  const [exportBusy, setExportBusy] = useState(false);
  const [control, setControl] = useState(null);
  const [subTab, setSubTab] = useState("por_factura");

  const loadControl = useCallback(async () => {
    setLoading(true);
    try {
      const data = await honorariosApi.getInvoiceControl({
        year: Number(year),
        source,
      });
      setControl(data);
    } catch (err) {
      setControl(null);
      toast.error(parseHonorariosError(err).message);
    } finally {
      setLoading(false);
    }
  }, [year, source]);

  useEffect(() => {
    loadControl();
  }, [loadControl]);

  const handleExport = async (view = "annual") => {
    if (!canExport) {
      toast.error("Sin permiso para exportar.");
      return;
    }
    setExportBusy(true);
    try {
      const response = await honorariosApi.exportInvoiceControl({
        year: Number(year),
        view,
        source,
      });
      const slug = { annual: "cobrado_anual", by_invoice: "por_factura", by_client: "por_cliente" }[view] || view;
      downloadBlobResponse(response, `honorarios_${slug}_${source}_${year}.xlsx`);
      toast.success("Excel descargado.");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setExportBusy(false);
    }
  };

  const kpis = control?.kpis || {};
  const byInvoice = control?.by_invoice || [];
  const byClient = control?.by_client || [];
  const matrix = control?.annual_matrix || [];
  const sourceLabel = CONTROL_SOURCES.find((s) => s.value === source)?.label || source;

  const invoiceColumns = useMemo(() => {
    const cols = [
      {
        key: "invoice_label",
        label: (source === "otros_pagos" || source === "eventuales") ? "Concepto / Factura" : "Factura",
      },
      { key: "client_name", label: "Cliente" },
      { key: "rfc", label: "RFC", render: (r) => r.rfc || "—" },
      { key: "issue_date", label: "Emisión" },
      {
        key: "collection_status",
        label: "Estado",
        render: (r) => collectionStatusLabel(r.collection_status) || r.status_bucket || "—",
      },
    ];
    if (source === "acumulado") {
      cols.splice(1, 0, {
        key: "origin",
        label: "Origen",
        render: (r) => ORIGIN_LABELS[r.origin] || r.origin || "—",
      });
    }
    if (canViewAmounts) {
      cols.push({ key: "total", label: "Emitido", render: (r) => formatMoney(r.total, true) });
      cols.push({
        key: "collected_amount",
        label: "Cobrado",
        render: (r) => formatMoney(r.collected_amount, true),
      });
      cols.push({
        key: "pending_amount",
        label: "Pendiente",
        render: (r) => formatMoney(r.pending_amount, true),
      });
    }
    return cols;
  }, [canViewAmounts, source]);

  const clientColumns = useMemo(() => {
    const cols = [
      { key: "client_name", label: "Cliente" },
      { key: "rfc", label: "RFC", render: (r) => r.rfc || "—" },
      { key: "emitted_count", label: "Emitidas" },
      { key: "collected_count", label: "Cobradas" },
      { key: "pending_count", label: "Pendientes" },
    ];
    if (canViewAmounts) {
      cols.push({
        key: "emitted_amount",
        label: "Monto emitido",
        render: (r) => formatMoney(r.emitted_amount, true),
      });
      cols.push({
        key: "collected_amount",
        label: "Monto cobrado",
        render: (r) => formatMoney(r.collected_amount, true),
      });
      cols.push({
        key: "pending_amount",
        label: "Monto pendiente",
        render: (r) => formatMoney(r.pending_amount, true),
      });
    }
    return cols;
  }, [canViewAmounts]);

  return (
    <div className="space-y-4" data-testid="honorarios-historial-control">
      <DespachoSectionCard title="Control de facturas">
        <p className="m-0 mb-3 text-[11px] text-slate-500">
          Resumen anual de honorarios emitidos, cobrados y pendientes según el origen seleccionado.
          El Excel anual incluye el nombre del despacho, periodo, origen seleccionado,
          montos con separador de miles, columna Cliente y meses remarcados, y totales por mes.
        </p>
        <div className="flex flex-wrap gap-2 mb-1 items-center">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-8 w-[110px] text-xs rounded-sm" aria-label="Año del control">
              <SelectValue placeholder="Año" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger
              className="h-8 w-[200px] text-xs rounded-sm"
              aria-label="Origen del control"
              data-testid="honorarios-historial-origen"
            >
              <SelectValue placeholder="Origen" />
            </SelectTrigger>
            <SelectContent>
              {CONTROL_SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canExport ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm h-8 text-xs"
              disabled={exportBusy || loading}
              onClick={() => handleExport("annual")}
              data-testid="honorarios-historial-export-anual"
            >
              {exportBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
              Excel anual ({sourceLabel})
            </Button>
          ) : null}
        </div>
        <p className="m-0 mb-3 text-[10px] text-slate-400">{SOURCE_HINTS[source]}</p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando control…
          </div>
        ) : (
          <>
            <div className="grid gap-2 sm:grid-cols-3 mb-4">
              <DespachoKpiCard
                label="Emitidas"
                value={
                  canViewAmounts
                    ? `${kpis.emitted?.count ?? 0} · ${formatMoney(kpis.emitted?.amount, true)}`
                    : String(kpis.emitted?.count ?? 0)
                }
                tone="neutral"
              />
              <DespachoKpiCard
                label="Cobradas"
                value={
                  canViewAmounts
                    ? `${kpis.collected?.count ?? 0} · ${formatMoney(kpis.collected?.amount, true)}`
                    : String(kpis.collected?.count ?? 0)
                }
                tone="info"
              />
              <DespachoKpiCard
                label="Pendientes por cobrar"
                value={
                  canViewAmounts
                    ? `${kpis.pending?.count ?? 0} · ${formatMoney(kpis.pending?.amount, true)}`
                    : String(kpis.pending?.count ?? 0)
                }
                tone="warn"
              />
            </div>

            <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
              <TabsList className="!flex h-auto w-full flex-wrap justify-start gap-1 mb-3">
                <TabsTrigger value="por_factura" className="text-xs rounded-sm" data-testid="historial-tab-por-factura">
                  Por factura
                </TabsTrigger>
                <TabsTrigger value="por_cliente" className="text-xs rounded-sm" data-testid="historial-tab-por-cliente">
                  Por cliente
                </TabsTrigger>
                <TabsTrigger value="matriz" className="text-xs rounded-sm" data-testid="historial-tab-matriz">
                  Cobrado por mes
                </TabsTrigger>
              </TabsList>

              <TabsContent value="por_factura" className="mt-0">
                {canExport ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs mb-2"
                    disabled={exportBusy}
                    onClick={() => handleExport("by_invoice")}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Exportar por factura
                  </Button>
                ) : null}
                <DespachoSimpleTable
                  columns={invoiceColumns}
                  rows={byInvoice.map((r) => ({ ...r, id: r.id || r.cfdi_uuid || r.invoice_label }))}
                  emptyLabel={`Sin registros en ${sourceLabel.toLowerCase()} para ${year}.`}
                />
              </TabsContent>

              <TabsContent value="por_cliente" className="mt-0">
                {canExport ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs mb-2"
                    disabled={exportBusy}
                    onClick={() => handleExport("by_client")}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Exportar por cliente
                  </Button>
                ) : null}
                <DespachoSimpleTable
                  columns={clientColumns}
                  rows={byClient.map((r) => ({ ...r, id: r.fiscal_client_id || r.rfc || r.client_name }))}
                  emptyLabel={`Sin clientes con movimientos en ${year}.`}
                />
              </TabsContent>

              <TabsContent value="matriz" className="mt-0">
                <div className="overflow-x-auto rounded-sm border border-slate-200">
                  <table className="w-full text-[11px] min-w-[900px]" data-testid="honorarios-historial-matriz">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left">
                        <th className="p-2 font-medium">Cliente</th>
                        <th className="p-2 font-medium">RFC</th>
                        {MONTH_LABELS.map((m) => (
                          <th key={m} className="p-2 font-medium whitespace-nowrap">{m}</th>
                        ))}
                        <th className="p-2 font-medium">Acumulado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.length ? matrix.map((row) => (
                        <tr key={row.fiscal_client_id || row.RFC || row.Cliente} className="border-b border-slate-100">
                          <td className="p-2 font-medium">{row.Cliente || "—"}</td>
                          <td className="p-2 font-mono">{row.RFC || "—"}</td>
                          {MONTH_KEYS.map((k) => (
                            <td key={k} className="p-2 whitespace-nowrap">
                              {canViewAmounts ? formatMoney(row[k], true) : "•••"}
                            </td>
                          ))}
                          <td className="p-2 whitespace-nowrap font-medium">
                            {canViewAmounts ? formatMoney(row.Acumulado, true) : "•••"}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={15} className="p-3 text-slate-500">
                            Sin cobros registrados en {year}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </DespachoSectionCard>
    </div>
  );
}
